import { useState } from "react";
import { useTranslation } from "react-i18next";
import { trpc } from "@/lib/trpc";
import DashboardLayout from "@/components/DashboardLayout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Bug,
  Lightbulb,
  HelpCircle,
  AlertCircle,
  MessageSquare,
  Loader2,
  CheckCircle,
  Clock,
  Archive,
  Eye,
  Trash2,
} from "lucide-react";
import { format } from "date-fns";
import { fr, enUS, ar } from "date-fns/locale";

const feedbackTypeIcons = {
  bug: Bug,
  suggestion: Lightbulb,
  question: HelpCircle,
  complaint: AlertCircle,
  other: MessageSquare,
};

const statusColors = {
  new: "bg-blue-500",
  in_progress: "bg-yellow-500",
  resolved: "bg-green-500",
  archived: "bg-gray-500",
};

const priorityColors = {
  low: "bg-gray-500",
  medium: "bg-blue-500",
  high: "bg-orange-500",
  urgent: "bg-red-500",
};

export default function AdminFeedback() {
  const { t, i18n } = useTranslation();
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [selectedFeedback, setSelectedFeedback] = useState<any>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [adminNotes, setAdminNotes] = useState("");
  const [newStatus, setNewStatus] = useState("");
  const [newPriority, setNewPriority] = useState("");

  const { data, isLoading, refetch } = trpc.feedback.list.useQuery({
    status: statusFilter as any,
    type: typeFilter as any,
    limit: 100,
  });

  const { data: stats } = trpc.feedback.getStats.useQuery();

  const updateStatus = trpc.feedback.updateStatus.useMutation({
    onSuccess: () => {
      toast.success(t("feedback.admin.update_success"));
      refetch();
      setDetailsOpen(false);
    },
    onError: () => {
      toast.error(t("feedback.admin.update_error"));
    },
  });

  const deleteFeedback = trpc.feedback.delete.useMutation({
    onSuccess: () => {
      toast.success(t("feedback.admin.delete_success"));
      refetch();
      setDetailsOpen(false);
    },
    onError: () => {
      toast.error(t("feedback.admin.delete_error"));
    },
  });

  const handleViewDetails = (feedback: any) => {
    setSelectedFeedback(feedback);
    setAdminNotes(feedback.admin_notes || "");
    setNewStatus(feedback.status);
    setNewPriority(feedback.priority || "medium");
    setDetailsOpen(true);
  };

  const handleUpdateStatus = () => {
    if (!selectedFeedback) return;

    updateStatus.mutate({
      id: selectedFeedback.id,
      status: newStatus as any,
      priority: newPriority as any,
      adminNotes: adminNotes.trim() || undefined,
    });
  };

  const handleDelete = () => {
    if (!selectedFeedback) return;
    if (!confirm(t("feedback.admin.delete_confirm"))) return;

    deleteFeedback.mutate({ id: selectedFeedback.id });
  };

  const getDateLocale = () => {
    switch (i18n.language) {
      case "fr":
        return fr;
      case "ar":
        return ar;
      default:
        return enUS;
    }
  };

  const TypeIcon = selectedFeedback
    ? feedbackTypeIcons[selectedFeedback.type as keyof typeof feedbackTypeIcons]
    : MessageSquare;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {t("feedback.admin.title")}
          </h1>
          <p className="text-muted-foreground mt-2">
            {t("feedback.admin.subtitle")}
          </p>
        </div>

        {/* Statistics */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">
                  {t("feedback.admin.stat_new")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.byStatus.new}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">
                  {t("feedback.admin.stat_in_progress")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {stats.byStatus.in_progress}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">
                  {t("feedback.admin.stat_resolved")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {stats.byStatus.resolved}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">
                  {t("feedback.admin.stat_total")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{data?.total || 0}</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle>{t("feedback.admin.filters")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("feedback.admin.filter_status")}</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      {t("feedback.admin.status_all")}
                    </SelectItem>
                    <SelectItem value="new">
                      {t("feedback.admin.status_new")}
                    </SelectItem>
                    <SelectItem value="in_progress">
                      {t("feedback.admin.status_in_progress")}
                    </SelectItem>
                    <SelectItem value="resolved">
                      {t("feedback.admin.status_resolved")}
                    </SelectItem>
                    <SelectItem value="archived">
                      {t("feedback.admin.status_archived")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t("feedback.admin.filter_type")}</Label>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      {t("feedback.admin.type_all")}
                    </SelectItem>
                    <SelectItem value="bug">
                      {t("feedback.type_bug")}
                    </SelectItem>
                    <SelectItem value="suggestion">
                      {t("feedback.type_suggestion")}
                    </SelectItem>
                    <SelectItem value="question">
                      {t("feedback.type_question")}
                    </SelectItem>
                    <SelectItem value="complaint">
                      {t("feedback.type_complaint")}
                    </SelectItem>
                    <SelectItem value="other">
                      {t("feedback.type_other")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Feedback List */}
        <Card>
          <CardHeader>
            <CardTitle>{t("feedback.admin.list_title")}</CardTitle>
            <CardDescription>
              {t("feedback.admin.list_description", {
                count: data?.total || 0,
              })}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            ) : data?.feedback.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                {t("feedback.admin.no_feedback")}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("feedback.admin.col_type")}</TableHead>
                    <TableHead>{t("feedback.admin.col_subject")}</TableHead>
                    <TableHead>{t("feedback.admin.col_user")}</TableHead>
                    <TableHead>{t("feedback.admin.col_status")}</TableHead>
                    <TableHead>{t("feedback.admin.col_priority")}</TableHead>
                    <TableHead>{t("feedback.admin.col_date")}</TableHead>
                    <TableHead>{t("feedback.admin.col_actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data?.feedback.map((feedback: any) => {
                    const Icon =
                      feedbackTypeIcons[
                        feedback.type as keyof typeof feedbackTypeIcons
                      ];
                    return (
                      <TableRow key={feedback.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Icon className="h-4 w-4" />
                            <span className="text-sm">
                              {t(`feedback.type_${feedback.type}`)}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="font-medium max-w-xs truncate">
                          {feedback.subject}
                        </TableCell>
                        <TableCell className="text-sm">
                          {feedback.user?.email || t("feedback.admin.anonymous")}
                        </TableCell>
                        <TableCell>
                          <Badge
                            className={`${
                              statusColors[
                                feedback.status as keyof typeof statusColors
                              ]
                            } text-white`}
                          >
                            {t(`feedback.admin.status_${feedback.status}`)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={
                              priorityColors[
                                feedback.priority as keyof typeof priorityColors
                              ]
                            }
                          >
                            {t(`feedback.admin.priority_${feedback.priority}`)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {format(
                            new Date(feedback.created_at),
                            "dd MMM yyyy",
                            { locale: getDateLocale() }
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewDetails(feedback)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TypeIcon className="h-5 w-5" />
              {selectedFeedback?.subject}
            </DialogTitle>
            <DialogDescription>
              {t("feedback.admin.details_subtitle")}
            </DialogDescription>
          </DialogHeader>

          {selectedFeedback && (
            <div className="space-y-4">
              {/* Metadata */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-muted rounded-lg">
                <div>
                  <p className="text-sm font-medium">
                    {t("feedback.admin.col_user")}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {selectedFeedback.user?.email ||
                      t("feedback.admin.anonymous")}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium">
                    {t("feedback.admin.col_date")}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {format(
                      new Date(selectedFeedback.created_at),
                      "dd MMM yyyy HH:mm",
                      { locale: getDateLocale() }
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium">
                    {t("feedback.admin.col_type")}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {t(`feedback.type_${selectedFeedback.type}`)}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium">
                    {t("feedback.admin.page_url")}
                  </p>
                  <p className="text-sm text-muted-foreground truncate">
                    {selectedFeedback.page_url || "N/A"}
                  </p>
                </div>
              </div>

              {/* Message */}
              <div className="space-y-2">
                <Label>{t("feedback.message_label")}</Label>
                <div className="p-4 bg-muted rounded-lg whitespace-pre-wrap">
                  {selectedFeedback.message}
                </div>
              </div>

              {/* Status and Priority */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("feedback.admin.col_status")}</Label>
                  <Select value={newStatus} onValueChange={setNewStatus}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">
                        {t("feedback.admin.status_new")}
                      </SelectItem>
                      <SelectItem value="in_progress">
                        {t("feedback.admin.status_in_progress")}
                      </SelectItem>
                      <SelectItem value="resolved">
                        {t("feedback.admin.status_resolved")}
                      </SelectItem>
                      <SelectItem value="archived">
                        {t("feedback.admin.status_archived")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t("feedback.admin.col_priority")}</Label>
                  <Select value={newPriority} onValueChange={setNewPriority}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">
                        {t("feedback.admin.priority_low")}
                      </SelectItem>
                      <SelectItem value="medium">
                        {t("feedback.admin.priority_medium")}
                      </SelectItem>
                      <SelectItem value="high">
                        {t("feedback.admin.priority_high")}
                      </SelectItem>
                      <SelectItem value="urgent">
                        {t("feedback.admin.priority_urgent")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Admin Notes */}
              <div className="space-y-2">
                <Label>{t("feedback.admin.admin_notes")}</Label>
                <Textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  rows={4}
                  placeholder={t("feedback.admin.admin_notes_placeholder")}
                />
              </div>

              {/* Actions */}
              <div className="flex justify-between">
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={deleteFeedback.isPending}
                >
                  {deleteFeedback.isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t("feedback.admin.delete_button")}
                </Button>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setDetailsOpen(false)}
                  >
                    {t("common.cancel")}
                  </Button>
                  <Button
                    onClick={handleUpdateStatus}
                    disabled={updateStatus.isPending}
                  >
                    {updateStatus.isPending && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    {t("feedback.admin.update_button")}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
